import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy, Info, Lock, AlertTriangle, ShieldAlert } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

/** Small presentational pieces shared by the List Management views. */

/**
 * A labelled identifier with a copy button (#769: the import ID support and
 * QA quote). The value is also `select-all`, so it can be copied by hand where
 * the clipboard API is unavailable.
 */
export function CopyId({ label, value, testId }) {
    const [copied, setCopied] = useState(false);
    const timer = useRef(null);
    useEffect(() => () => clearTimeout(timer.current), []);
    if (!value) return null;
    const copy = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard unavailable: the value stays selectable */ }
    };
    const Icon = copied ? Check : Copy;
    return (
        <span className="inline-flex items-center gap-1.5 text-[11.5px] max-w-full" data-testid={testId}>
            <span className="text-muted-foreground whitespace-nowrap">{label}</span>
            <span className="font-mono text-foreground select-all break-all">{value}</span>
            <button type="button" onClick={copy} aria-label={`Copy ${label}`} title={copied ? 'Copied' : 'Copy'}
                    className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded text-muted-foreground hover:bg-muted hover:text-foreground">
                <Icon className="w-3 h-3" aria-hidden="true" />
            </button>
        </span>
    );
}

/**
 * Colour of the marks that carry no words (the empty-cell dash, the idle sort arrows, the search glass, the empty-state
 * icon). The shell's third ink is 2.7:1 on Porcelain and 3.35:1 on Graphite, under the 3:1 a graphic needs; the muted foreground at
 * 80% is 3.1:1+ on every surface the table paints (card, header, selected row) in both themes and keeps the dash light.
 * Informational text never uses it.
 */
export const DECOR_TEXT = 'text-muted-foreground/80';

/**
 * Status tones as explicit light/dark pairs (each text colour keeps >= 4.5:1 on its
 * own soft background in BOTH themes; the Graphite pairs use the shell's status
 * tokens). Name a tone with `{ tone: 'good' }`.
 */
export const TONE_CLASSES = {
    good: 'bg-emerald-50 dark:bg-good-bg text-emerald-800 dark:text-good',
    warn: 'bg-amber-50 dark:bg-warn-bg text-amber-800 dark:text-warn',
    bad: 'bg-red-50 dark:bg-bad-bg text-red-800 dark:text-bad',
    neutral: 'bg-muted text-ink-2',
    accent: 'bg-violet-50 dark:bg-violet-500/15 text-violet-700 dark:text-violet-300',
    info: 'bg-cyan-50 dark:bg-cyan-500/15 text-cyan-700 dark:text-cyan-300',
};

/**
 * Resolve a tone object (`{ tone: 'good' }`) to its class pair. An unknown shape,
 * including the retired `{ color, bg }` hex pair, renders as the neutral tone so a
 * light-theme colour can never reach the DOM.
 */
export function toneClasses(colors) {
    if (colors && Object.prototype.hasOwnProperty.call(TONE_CLASSES, colors.tone)) return TONE_CLASSES[colors.tone];
    return TONE_CLASSES.neutral;
}

/** The keyboard focus ring for a tooltip trigger: an outline, so focus never changes a width (#830). */
export const FOCUS_RING = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring';

/**
 * The shared List Management tooltip trigger (#975): Radix's tooltip from the
 * shared `ui/tooltip.jsx`, with a one-sentence description and optional detail
 * lines. Without a description it renders `children` unchanged, so a caller
 * can pass one only when it has something to say. A trigger is a Tab stop
 * (owner decision D1): it shows the tooltip on keyboard focus and Esc closes
 * it. Hover and focus only — Radix does not open on touch, and no touch
 * handler is added (D2).
 */
export function LmTip({ description, detail, side, open, onOpenChange, children }) {
    const controlled = open !== undefined;
    const [requested, setRequested] = useState(false);
    const want = controlled ? open : requested;
    // Focusing an off-screen trigger (Tab into a wide or long table) scrolls it
    // into view, and Radix's content closes its tooltip when the trigger
    // scrolls, so a keyboard user's tooltip would flash and vanish. Radix is
    // told the tooltip is open at once (so a blur or pointer-leave still closes
    // it), but the content mounts two frames later, once that scroll has fired;
    // a later scroll still closes it.
    const [settled, setSettled] = useState(false);
    useEffect(() => {
        if (!want) { setSettled(false); return undefined; }
        let frame = requestAnimationFrame(() => { frame = requestAnimationFrame(() => setSettled(true)); });
        return () => cancelAnimationFrame(frame);
    }, [want]);
    if (!description) return children;
    const change = (next) => (controlled ? onOpenChange?.(next) : setRequested(next));
    return (
        <Tooltip open={want} onOpenChange={change}>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            {(settled || !want) && (
                <TooltipContent side={side} collisionPadding={8}
                                className="max-w-[300px] text-[12px] leading-snug" data-testid="lm-tip">
                    <p>{description}</p>
                    {(detail || []).map((line, i) => <p key={i}>{line}</p>)}
                </TooltipContent>
            )}
        </Tooltip>
    );
}

/**
 * A status badge. With a `description` it is a Tab stop (D1) whose tooltip
 * opens on hover and focus; without one it is the plain span it always was.
 * No native `title`: #975 removes it from headers and badges (Pill's old
 * `title` prop is gone — pass `description` / `detail` instead).
 */
export function Pill({ label, tone, description, detail, testId }) {
    const [text, colors] = Array.isArray(label) ? label : [label, tone];
    const pill = (
        <span
            data-testid={testId}
            tabIndex={description ? 0 : undefined}
            className={`inline-flex items-center h-[22px] px-2 rounded-md text-[11.5px] font-semibold whitespace-nowrap ${toneClasses(colors)}${description ? ` ${FOCUS_RING}` : ''}`}
        >
            {text}
        </span>
    );
    return <LmTip description={description} detail={detail}>{pill}</LmTip>;
}

export function CountPill({ count, active = false }) {
    return (
        <span
            className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11.5px] font-semibold tabular-nums ${active ? 'bg-violet-700 text-white' : 'bg-muted text-ink-2'}`}
        >
            {count}
        </span>
    );
}

/** The banner every pre-CRM view carries (#770, #773). */
export function NoCrmWriteBanner({ testId = 'lm-no-crm-banner' }) {
    return (
        <div
            data-testid={testId}
            role="note"
            className="flex items-center gap-2.5 mb-3.5 px-3.5 py-2.5 border border-border rounded-[10px] bg-card text-[12.5px] text-ink-2"
        >
            <Info className="w-3.5 h-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
            No CRM records change until an Admin confirms Commit.
        </div>
    );
}

/** Prototype copy for the read-only Imports view. */
export function ReadOnlyBanner() {
    return (
        <div
            data-testid="lm-readonly-banner"
            role="note"
            className="flex items-center gap-2.5 mb-3.5 px-3.5 py-2.5 border border-border rounded-[10px] bg-card text-[12.5px] text-ink-2"
        >
            <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
            Imported records are read-only. Move them to Staging to clean up, edit or assign.
        </div>
    );
}

export function StateMessage({ kind, title, detail, action }) {
    const Icon = kind === 'unauthorized' || kind === 'step_up' ? ShieldAlert : kind === 'error' ? AlertTriangle : Info;
    return (
        <div className="py-14 text-center" role={kind === 'error' ? 'alert' : 'status'} data-testid={`lm-state-${kind}`}>
            <Icon className={`w-5 h-5 mx-auto mb-2 ${DECOR_TEXT}`} aria-hidden="true" />
            <div className="text-[13.5px] font-medium text-foreground">{title}</div>
            {detail && <div className="mt-1 text-[12.5px] text-muted-foreground">{detail}</div>}
            {action && <div className="mt-3">{action}</div>}
        </div>
    );
}

export const GhostButton = React.forwardRef(function GhostButton(
    { children, onClick, disabled, title, icon: Icon, testId, ariaLabel, className = '', ...rest }, ref,
) {
    return (
        <button
            ref={ref}
            type="button"
            {...rest}
            onClick={onClick}
            disabled={disabled}
            aria-disabled={disabled || undefined}
            title={title}
            aria-label={ariaLabel}
            data-testid={testId}
            className={`inline-flex items-center gap-2 h-[38px] px-[13px] border border-input rounded-[9px] bg-card text-[13px] font-medium text-foreground whitespace-nowrap hover:bg-muted disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-card ${className}`}
        >
            {Icon && <Icon className="w-[15px] h-[15px] text-muted-foreground" aria-hidden="true" />}
            {children}
        </button>
    );
});

export function PrimaryButton({ children, onClick, disabled, title, icon: Icon, testId, tone = 'accent', type = 'button' }) {
    const bg = tone === 'dark'
        ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
        : 'bg-violet-700 hover:bg-violet-800 text-white';
    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            aria-disabled={disabled || undefined}
            title={title}
            data-testid={testId}
            className={`inline-flex items-center gap-2 h-[38px] px-[15px] rounded-[9px] text-[13px] font-semibold whitespace-nowrap ${bg} disabled:opacity-40 disabled:cursor-not-allowed`}
        >
            {Icon && <Icon className="w-[15px] h-[15px]" aria-hidden="true" />}
            {children}
        </button>
    );
}

export function Checkbox({ checked, indeterminate = false, onChange, label, disabled }) {
    const ref = React.useRef(null);
    React.useEffect(() => {
        if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
    }, [indeterminate, checked]);
    return (
        <input
            ref={ref}
            type="checkbox"
            className="lm-checkbox w-[15px] h-[15px] rounded accent-violet-700 dark:accent-violet-400 cursor-pointer disabled:cursor-default disabled:opacity-60"
            checked={!!checked}
            onChange={onChange}
            onClick={(e) => e.stopPropagation()}
            aria-label={label}
            aria-checked={indeterminate && !checked ? 'mixed' : !!checked}
            disabled={disabled}
        />
    );
}
